import React, { useEffect, useState } from 'react';
import { getAllStationTypes, StationType } from '../Firebase/firebaseApi.ts';
import { trackEvent } from '../Analytics/posthog.ts';
import { getAuth } from 'firebase/auth';
import { Card, CardActionArea, CardMedia, CardContent, Typography, Container, Box, CircularProgress } from '@mui/material';
import './ChooseStationType.css';

interface ChooseStationTypeProps {
  onTypeSelected: (type: StationType) => void;
}

// Function to get available slot types from payLinks
function getAvailableSlotTypes(stationType: StationType): string[] {
  const availableTypes: string[] = [];
  if (stationType.payLinks.hour && stationType.payLinks.hour.trim() !== '') {
    availableTypes.push('hour');
  }
  if (stationType.payLinks.day && stationType.payLinks.day.trim() !== '') {
    availableTypes.push('day');
  }
  if (stationType.payLinks.month && stationType.payLinks.month.trim() !== '') {
    availableTypes.push('month');
  }
  return availableTypes;
}

function availableSlotTypesToHebrew(stationType: StationType): string {
  const availableTypes = getAvailableSlotTypes(stationType);
  const map: Record<string, string> = {
    hour: 'לשעה',
    day: 'ליום',
    month: 'לחודש',
  };
  return availableTypes.map(type => map[type] || type).join(' / ');
}

// Function to format prices with Hebrew text
function formatPricesDisplay(stationType: StationType): { text: string; prices: Array<{price: number, unit: string}> } {
  const prices: Array<{price: number, unit: string}> = [];
  
  if (stationType.pricePerBaseHour) {
    prices.push({ price: stationType.pricePerBaseHour, unit: 'לחבילת בסיס שעתית' });
  }
  if (stationType.pricePerDay) {
    prices.push({ price: stationType.pricePerDay, unit: 'ליום' });
  }
  if (stationType.pricePerMonth) {
    prices.push({ price: stationType.pricePerMonth, unit: 'לחודש' });
  }
  
  if (prices.length === 0) {
    return { text: '', prices: [] };
  }
  
  const priceTexts = prices.map(p => `${p.price} ₪ ${p.unit}`);
  return { text: priceTexts.join(' • '), prices };
}

const ChooseStationType: React.FC<ChooseStationTypeProps> = ({ onTypeSelected }) => {
  const [types, setTypes] = useState<StationType[]>([]);
  const [loading, setLoading] = useState(true);

  const auth = getAuth();
  const user = auth.currentUser;

  useEffect(() => {
    getAllStationTypes().then((data) => {
      setTypes(data);
      setLoading(false);
      
      // Track available station types shown to user
      trackEvent('station_types_displayed', {
        user_email: user?.email,
        station_types_count: data.length,
        station_types: data.map(type => ({
          id: type.id,
          name: type.typeFriendlyName,
          price_per_hour: type.pricePerBaseHour,
          price_per_day: type.pricePerDay,
          price_per_month: type.pricePerMonth,
          available_slot_types: getAvailableSlotTypes(type),
        })),
      });
    });
  }, [user?.email]);

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 8, direction: 'rtl' }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
          <CircularProgress />
          <Typography variant="h6" sx={{ ml: 2 }}>
            טוען סוגי חדרים...
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 2, direction: 'rtl' }}>
      <Box 
        sx={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
          gap: 3, 
          justifyItems: 'center' 
        }}
      >
        {types.map((type) => (
          <Card 
            key={type.id}
            sx={{ 
              width: '100%',
              maxWidth: 350,
              textAlign: 'right', 
              direction: 'rtl',
              transition: 'transform 0.2s, box-shadow 0.2s',
              display: 'flex',
              flexDirection: 'column',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 4
              }
            }}
          >
            <CardActionArea onClick={() => {
              // Track station type interaction before selection
              trackEvent('station_type_clicked', {
                user_email: user?.email,
                station_type_id: type.id,
                station_type_name: type.typeFriendlyName,
                price_per_hour: type.pricePerBaseHour,
                price_per_day: type.pricePerDay,
                price_per_month: type.pricePerMonth,
                available_slot_types: getAvailableSlotTypes(type),
              });
              
              onTypeSelected(type);
            }} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              {type.imageLink && (
                <CardMedia 
                  component="img" 
                  height="180" 
                  image={type.imageLink} 
                  alt={type.typeFriendlyName}
                  sx={{ objectFit: 'cover' }}
                />
              )}
              <CardContent sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', p: 3, minHeight: 160, flexGrow: 1 }}>
                <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 'bold', mb: 2 }}>
                  {type.typeFriendlyName}
                </Typography>
                <Typography variant="body1" sx={{ color: 'success.main', mb: 1 }}>
                  סוגי השכרה: {availableSlotTypesToHebrew(type)}
                </Typography>
                {formatPricesDisplay(type).prices.length > 0 && (
                  <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, justifyContent: 'center' }}>
                      {formatPricesDisplay(type).prices.map((priceInfo, index) => (
                        <Box key={index} sx={{ textAlign: 'center' }}>
                          <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main', lineHeight: 1 }}>
                            {priceInfo.price} ₪
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                            {priceInfo.unit}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}
              </CardContent>
            </CardActionArea>
          </Card>
        ))}
      </Box>
    </Container>
  );
};

export default ChooseStationType;
